import React from 'react';

import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import {
  AddOutlined as AddIcon,
  CloseOutlined as CloseIcon,
  DeleteOutline as DeleteIcon,
  SaveOutlined as SaveIcon,
} from '@mui/icons-material';

import { GridColDef } from '@mui/x-data-grid';

import { FDataGrid } from '../grid';
import { ComboRow } from '../core/coremodels';

import { BudgetLineRow, TaskRow } from './activityplanning.models';

import {
  fetchBudgetLines,
  fetchTaskCandidates,
  fetchTaskRepository,
  saveBudgetLines,
  savePlanningTasks,
} from './activityplanning.apis';

type Props = {
  open: boolean;

  activity: any | null;
  taskRows: TaskRow[];

  accounts?: ComboRow[];

  planningCycleName?: string;
  costCentreName?: string;

  onClose: () => void;

  /*
   * Called after successful persistence so the parent
   * Activity Planning workspace can refresh its
   * authoritative AP-014/workbench state.
   */
  onSaved?: () => Promise<void> | void;
};

type State = {
  tasks: TaskRow[];
  selectedTask: TaskRow | null;

  /*
   * Local working BudgetLine cache.
   *
   * taskplanid -> BudgetLine[]
   */
  budgets: Record<string, BudgetLineRow[]>;

  /*
   * Dirty state is maintained independently per TaskPlan.
   *
   * This allows the dialog to retain local BudgetLine
   * changes while the planner moves between Tasks.
   */
  dirtyBudgets: Record<string, boolean>;

  loadingBudget: boolean;
  saving: boolean;
  error: string;

  addExistingOpen: boolean;
  createTaskOpen: boolean;

  taskCandidates: TaskRow[];
  loadingCandidates: boolean;
  selectedCandidateId: number | null;

  newTaskName: string;
  newTaskDescription: string;
  newTaskWeight: number;

  /*
   * Selected BudgetLine is used only for UI deletion.
   */
  selectedBudgetLineId: string | number | null;
};

export default class TaskPlanningDialog extends React.Component<Props, State> {
  state: State = {
    tasks: [],
    selectedTask: null,

    budgets: {},
    dirtyBudgets: {},

    loadingBudget: false,
    saving: false,
    error: '',

    addExistingOpen: false,
    createTaskOpen: false,

    taskCandidates: [],
    loadingCandidates: false,
    selectedCandidateId: null,

    newTaskName: '',
    newTaskDescription: '',
    newTaskWeight: 1,

    selectedBudgetLineId: null,
  };

  componentDidMount() {
    if (this.props.open) {
      this.initialize();
    }
  }

  componentDidUpdate(prevProps: Props) {
    const opened = this.props.open && !prevProps.open;

    const activityChanged =
      this.props.open &&
      Number(prevProps.activity?.activityplanid ?? 0) !==
        Number(this.props.activity?.activityplanid ?? 0);

    if (opened || activityChanged) {
      this.initialize();
    }
  }

  /*
   * -------------------------------------------------------------------------
   * Context helpers
   * -------------------------------------------------------------------------
   */

  private activityPlanId = (): number =>
    Number(
      this.props.activity?.activityplanid ?? this.props.activity?.rid ?? 0
    );

  /*
   * Generic row identity helper.
   *
   * This is suitable for DataGrid/cache identity.
   * Financial persistence itself always uses taskplanid explicitly.
   */
  private taskRowId = (task: TaskRow) =>
    (task as any).taskplanid ?? (task as any).rid ?? (task as any).id;

  private taskKey = (task: TaskRow): string => String(this.taskRowId(task));

  private selectedBudgetRows = (): BudgetLineRow[] => {
    const task = this.state.selectedTask;

    if (!task) {
      return [];
    }

    return this.state.budgets[this.taskKey(task)] || [];
  };

  private selectedBudgetDirty = (): boolean => {
    const task = this.state.selectedTask;

    if (!task) {
      return false;
    }

    return !!this.state.dirtyBudgets[this.taskKey(task)];
  };

  /*
   * Central BudgetLine working-set updater.
   *
   * Every local BudgetLine mutation should pass through here.
   */
  private setSelectedBudgetRows = (rows: BudgetLineRow[], dirty = true) => {
    const task = this.state.selectedTask;

    if (!task) {
      return;
    }

    /*
     * Budget persistence belongs to a seasonal TaskPlan.
     * Do not fall back to Task Master identity here.
     */
    const taskplanid = Number(task.taskplanid ?? 0);

    if (!taskplanid) {
      return;
    }

    const key = String(taskplanid);

    this.setState((state) => ({
      budgets: {
        ...state.budgets,
        [key]: rows,
      },

      dirtyBudgets: {
        ...state.dirtyBudgets,
        [key]: dirty,
      },
    }));
  };

  /*
   * -------------------------------------------------------------------------
   * Initialization
   * -------------------------------------------------------------------------
   */

  // private initialize = () => {
  //   const tasks = [...(this.props.taskRows || [])];

  //   this.setState(
  //     {
  //       tasks,

  //       selectedTask: tasks.length > 0 ? tasks[0] : null,

  //       /*
  //        * A newly opened Activity starts with a fresh
  //        * local BudgetLine working cache.
  //        */
  //       budgets: {},
  //       dirtyBudgets: {},

  //       loadingBudget: false,
  //       saving: false,
  //       error: '',

  //       addExistingOpen: false,
  //       createTaskOpen: false,

  //       taskCandidates: [],
  //       loadingCandidates: false,
  //       selectedCandidateId: null,

  //       newTaskName: '',
  //       newTaskDescription: '',
  //       newTaskWeight: 1,

  //       selectedBudgetLineId: null,
  //     },
  //     async () => {
  //       if (this.state.selectedTask) {
  //         await this.ensureBudgetLoaded(this.state.selectedTask);
  //       }
  //     }
  //   );
  // };
  private initialize = async () => {
    const activityplanid = this.activityPlanId();

    this.setState({
      tasks: [],
      selectedTask: null,

      budgets: {},
      dirtyBudgets: {},

      loadingBudget: false,
      saving: false,
      error: '',

      addExistingOpen: false,
      createTaskOpen: false,

      taskCandidates: [],
      loadingCandidates: false,
      selectedCandidateId: null,

      newTaskName: '',
      newTaskDescription: '',
      newTaskWeight: 1,

      selectedBudgetLineId: null,
    });

    if (!activityplanid) {
      return;
    }

    try {
      // TODO:
      // const rows = await fetchPlanningTasks(activityplanid);

      // // seasonal TaskPlans
      // const plannedTasks = rows.filter(
      //   row => row.taskplanid != null
      // );

      // // reusable Tasks not yet planned
      // const availableTasks = rows.filter(
      //   row => row.taskplanid == null
      // );

      const rows = await fetchTaskRepository(activityplanid);

      const tasks = rows.filter(
        (row) => row.taskplanid != null
      );

      const selectedTask = tasks[0] ?? null;

      this.setState(
        {
          tasks,
          selectedTask,
        },
        () => {
          if (selectedTask) {
            void this.ensureBudgetLoaded(selectedTask);
          }
        }
      );
    } catch (error: any) {
      this.setState({
        error: error?.message || String(error),
      });
    }
  };

  /*
   * -------------------------------------------------------------------------
   * Budget loading
   * -------------------------------------------------------------------------
   */

  private ensureBudgetLoaded = async (task: TaskRow) => {
    const taskplanid = Number(task.taskplanid ?? 0);

    /*
     * A repository-only Task has no seasonal budget.
     */
    if (!taskplanid) {
      return;
    }

    const key = String(taskplanid);

    /*
     * [] is a legitimate loaded result.
     *
     * Therefore use property existence rather than
     * checking array length.
     */
    if (Object.prototype.hasOwnProperty.call(this.state.budgets, key)) {
      return;
    }

    this.setState({
      loadingBudget: true,
      error: '',
    });

    try {
      const rows = await fetchBudgetLines(taskplanid);

      this.setState((state) => ({
        budgets: {
          ...state.budgets,
          [key]: rows,
        },

        dirtyBudgets: {
          ...state.dirtyBudgets,
          [key]: false,
        },
      }));
    } catch (error: any) {
      this.setState({
        error: error?.message || String(error),
      });
    } finally {
      this.setState({
        loadingBudget: false,
      });
    }
  };

  // private selectTask = (task: TaskRow | null) => {
  //   this.setState({
  //     selectedTask: task,
  //     selectedBudgetLineId: null,
  //     error: '',
  //   });

  //   if (task) {
  //     void this.ensureBudgetLoaded(task);
  //   }
  // };
  private selectTask = (task: TaskRow | null) => {
    if (!task) {
      return;
    }

    const current = this.state.selectedTask;

    if (
      current &&
      Number(current.taskplanid ?? 0) ===
      Number(task.taskplanid ?? 0)
    ) {
      return;
    }

    this.setState({
      selectedTask: task,
      selectedBudgetLineId: null,
      error: '',
    });

    void this.ensureBudgetLoaded(task);
  };

  /*
   * -------------------------------------------------------------------------
   * Formatting / summary
   * -------------------------------------------------------------------------
   */

  private money = (value: any) =>
    Number(value || 0).toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });

  private summary = () => {
    const tasks = this.state.tasks || [];

    const total = tasks.reduce(
      (sum, task: any) => sum + Number(task.plannedamount ?? 0),
      0
    );

    const budgeted = tasks.filter(
      (task: any) => Number(task.plannedamount ?? 0) > 0
    ).length;

    return {
      taskCount: tasks.length,

      budgeted,

      unbudgeted: tasks.length - budgeted,

      total,
    };
  };

  /*
   * -------------------------------------------------------------------------
   * Task grid
   * -------------------------------------------------------------------------
   */

  private taskColumns = (): GridColDef[] => [
    {
      field: 'code',
      headerName: 'Task Code',
      width: 175,
    },
    {
      field: 'name',
      headerName: 'Task',
      minWidth: 420,
      flex: 1,
    },
    {
      field: 'weight',
      headerName: 'Weight',
      width: 100,
      type: 'number',
    },
    {
      field: 'plannedstartdate',
      headerName: 'Start',
      width: 125,
      editable: true,
    },
    {
      field: 'plannedenddate',
      headerName: 'End',
      width: 125,
      editable: true,
    },
    // {
    //   field: 'plannedstartdate',
    //   headerName: 'Start',
    //   width: 125,
    //   editable: true,
    //   type: 'date',

    //   valueGetter: (value: any) =>
    //     value ? new Date(`${value}T00:00:00`) : null,

    //   valueSetter: (value: any, row: any) => ({
    //     ...row,

    //     plannedstartdate:
    //       value instanceof Date
    //         ? value.toISOString().slice(0, 10)
    //         : value || null,
    //   }),
    // },
    // {
    //   field: 'plannedenddate',
    //   headerName: 'End',
    //   width: 125,
    //   editable: true,
    //   type: 'date',

    //   valueGetter: (value: any) =>
    //     value ? new Date(`${value}T00:00:00`) : null,

    //   valueSetter: (value: any, row: any) => ({
    //     ...row,

    //     plannedenddate:
    //       value instanceof Date
    //         ? value.toISOString().slice(0, 10)
    //         : value || null,
    //   }),
    // },
    // {
    //   field: 'plannedamount',
    //   headerName: 'Budget',
    //   width: 175,
    //   align: 'right',
    //   headerAlign: 'right',

    //   valueFormatter: (value: any) => this.money(value),
    // },
    {
      field: 'budgetstate',
      headerName: 'Budget State',
      width: 140,

      renderCell: (params) => {
        const amount = Number(params.row?.plannedamount ?? 0);

        return (
          <Chip
            size="small"
            variant="outlined"
            label={amount > 0 ? 'Budgeted' : 'No Budget'}
          />
        );
      },
    },
  ];

  private updateTaskRow = async (
    newRow: TaskRow,
    oldRow: TaskRow
  ): Promise<TaskRow> => {
    const activityplanid = this.activityPlanId();

    if (!activityplanid || !newRow.taskid) {
      return oldRow;
    }

    const start = newRow.plannedstartdate ?? null;
    const end = newRow.plannedenddate ?? null;

    if (start && end && start > end) {
      throw new Error(
        'Task planned end date cannot precede planned start date.'
      );
    }

    try {
      await savePlanningTasks(activityplanid, [
        {
          taskid: Number(newRow.taskid),
          weight: Number(newRow.weight ?? 1),
          plannedstartdate: start,
          plannedenddate: end,
        },
      ]);

      /*
       * Keep dialog working state synchronized with the
       * committed DataGrid row.
       */
      this.setState((state) => ({
        tasks: state.tasks.map((task) =>
          Number(task.taskplanid) === Number(newRow.taskplanid)
            ? newRow
            : task
        ),

        selectedTask:
          state.selectedTask &&
            Number(state.selectedTask.taskplanid) === Number(newRow.taskplanid)
            ? newRow
            : state.selectedTask,
      }));

      /*
       * Parent refresh is still useful for Programme/workbench
       * projections, but the dialog doesn't depend on it.
       */
      await this.props.onSaved?.();

      return newRow;

    } catch (error: any) {
      this.setState({
        error: error?.message || String(error),
      });

      throw error;
    }
  };

  // private updateTaskRow = async (
  //   newRow: TaskRow,
  //   oldRow: TaskRow
  // ): Promise<TaskRow> => {
  //   const activityplanid = this.activityPlanId();

  //   if (!activityplanid || !newRow.taskid) {
  //     return oldRow;
  //   }

  //   const start = newRow.plannedstartdate ?? null;
  //   const end = newRow.plannedenddate ?? null;

  //   if (start && end && start > end) {
  //     throw new Error(
  //       'Task planned end date cannot precede planned start date.'
  //     );
  //   }

  //   try {
  //     await savePlanningTasks(activityplanid, [
  //       {
  //         taskid: Number(newRow.taskid),

  //         /*
  //          * Preserve the existing seasonal planning values while
  //          * updating the schedule.
  //          */
  //         weight: Number(newRow.weight ?? 1),

  //         plannedstartdate: start,
  //         plannedenddate: end,
  //       },
  //     ]);

  //     /*
  //      * Reload from DB rather than trusting the local projection.
  //      * tasksave() remains authoritative.
  //      */
  //     await this.reloadTasks();

  //     await this.props.onSaved?.();

  //     return newRow;
  //   } catch (error: any) {
  //     this.setState({
  //       error: error?.message || String(error),
  //     });

  //     throw error;
  //   }
  // };

  /*
   * -------------------------------------------------------------------------
   * Budget grid
   * -------------------------------------------------------------------------
   */

  private budgetColumns = (): GridColDef[] => [
    {
      field: 'recno',
      headerName: '#',
      width: 65,
    },

    /*
     * Suggested COA remains read-only for this pass.
     *
     * A governed account selector can be introduced
     * separately without blocking AP-091E.
     */
    // {
    //   field: 'accountcode',
    //   headerName: 'Suggested COA',
    //   width: 160,

    //   valueFormatter: (value: any) => value || '—',
    // },
    {
      field: 'accountid',
      headerName: 'Account / COA',
      minWidth: 260,
      flex: 0.6,
      type: 'singleSelect',
      editable: true,

      valueOptions: [
        {
          value: null,
          label: '— No Account —',
        },

        ...(this.props.accounts || []).map((account) => ({
          value: Number(account.rid),
          label: account.shc ? `${account.shc} — ${account.nam}` : account.nam,
        })),
      ],

      valueFormatter: (value: any) => {
        if (value === null || value === undefined || value === '') {
          return '—';
        }

        const account = (this.props.accounts || []).find(
          (x) => Number(x.rid) === Number(value)
        );

        if (!account) {
          return '—';
        }

        return account.shc ? `${account.shc} — ${account.nam}` : account.nam;
      },
    },

    {
      field: 'description',
      headerName: 'Description',
      minWidth: 360,
      flex: 1,
      editable: true,
    },

    {
      field: 'quantity',
      headerName: 'Qty',
      width: 100,
      type: 'number',
      editable: true,
    },

    {
      field: 'unitprice',
      headerName: 'Unit Price',
      width: 160,
      type: 'number',
      editable: true,
      align: 'right',
      headerAlign: 'right',

      valueFormatter: (value: any) => this.money(value),
    },

    /*
     * Display only.
     *
     * Client projects quantity × unitprice for immediate
     * feedback, but budgetsave() remains authoritative.
     */
    {
      field: 'amount',
      headerName: 'Amount',
      width: 175,
      align: 'right',
      headerAlign: 'right',

      valueFormatter: (value: any) => this.money(value),
    },

    {
      field: 'remarks',
      headerName: 'Remarks',
      minWidth: 220,
      flex: 0.5,
      editable: true,
    },
  ];

  /*
   * Called by DataGrid after an editable BudgetLine row
   * has been committed.
   */
  // private updateBudgetRow = (newRow: BudgetLineRow): BudgetLineRow => {
  //   const quantity = Math.max(0, Number(newRow.quantity ?? 0));

  //   const unitprice = Math.max(0, Number(newRow.unitprice ?? 0));

  //   const updated: BudgetLineRow = {
  //     ...newRow,

  //     quantity,
  //     unitprice,

  //     /*
  //      * UI projection only.
  //      *
  //      * DB budgetsave() recalculates amount authoritatively.
  //      */
  //     amount: quantity * unitprice,

  //     _state: newRow._state === 'new' ? 'new' : 'dirty',
  //   };

  //   const rows = this.selectedBudgetRows().map((row: any) =>
  //     String(row.id) === String(updated.id) ? updated : row
  //   );

  //   this.setSelectedBudgetRows(rows, true);

  //   return updated;
  // };
  private updateBudgetRow = (newRow: BudgetLineRow): BudgetLineRow => {
    const quantity = Math.max(0, Number(newRow.quantity ?? 0));

    const unitprice = Math.max(0, Number(newRow.unitprice ?? 0));

    const accountid =
      newRow.accountid === null || newRow.accountid === undefined
        ? null
        : Number(newRow.accountid);

    const account = accountid
      ? (this.props.accounts || []).find((x) => Number(x.rid) === accountid)
      : undefined;

    const updated: BudgetLineRow = {
      ...newRow,

      accountid,

      accountcode: account?.shc ?? '',

      accountname: account?.nam ?? '',

      quantity,
      unitprice,

      /*
       * UI projection only.
       * budgetsave() remains authoritative.
       */
      amount: quantity * unitprice,

      _state: newRow._state === 'new' ? 'new' : 'dirty',
    };

    const rows = this.selectedBudgetRows().map((row: BudgetLineRow) =>
      String(row.id) === String(updated.id) ? updated : row
    );

    this.setSelectedBudgetRows(rows, true);

    return updated;
  };

  private addBudgetLine = () => {
    const task = this.state.selectedTask;

    if (!task) {
      return;
    }

    const taskplanid = Number(task.taskplanid ?? 0);

    if (!taskplanid) {
      this.setState({
        error: 'The selected Task does not have a seasonal Task Plan.',
      });

      return;
    }

    const rows = this.selectedBudgetRows();

    const nextRecno =
      rows.reduce((max, row) => Math.max(max, Number(row.recno ?? 0)), 0) + 1;

    /*
     * Temporary UI identity.
     *
     * The DB generates the permanent BudgetLine identity.
     */
    const id = `new-${taskplanid}-${nextRecno}`;

    const row = {
      id,

      rid: null,
      budgetlineid: null,

      taskplanid,
      taskid: task.taskid ?? null,

      accountid: null,
      accountcode: '',
      accountname: '',

      name: '',
      shortcode: '',

      description: '',

      quantity: 1,
      unitprice: 0,
      amount: 0,

      remarks: '',
      recno: nextRecno,

      status: 1,
      stamp: '',

      _state: 'new',

      raw: {},
    } as BudgetLineRow;

    this.setSelectedBudgetRows([...rows, row], true);

    this.setState({
      selectedBudgetLineId: id,
    });
  };

  /*
   * Deletion is intentionally a local complete-set operation.
   *
   * Existing rows are not sent with status=0.
   * Instead they are omitted from the complete set submitted
   * to budgetsave(), which retires them authoritatively.
   */
  private removeSelectedBudgetLine = () => {
    const id = this.state.selectedBudgetLineId;

    if (id === null || id === undefined) {
      return;
    }

    const rows = this.selectedBudgetRows().filter(
      (row: any) => String(row.id) !== String(id)
    );

    /*
     * rows=[] is legitimate.
     *
     * Saving [] retires all active BudgetLines for
     * the selected TaskPlan.
     */
    this.setSelectedBudgetRows(rows, true);

    this.setState({
      selectedBudgetLineId: null,
    });
  };

  /*
   * -------------------------------------------------------------------------
   * Budget persistence
   * -------------------------------------------------------------------------
   */

  // private saveSelectedBudget = async () => {
  //   const task = this.state.selectedTask;

  //   if (!task) {
  //     return;
  //   }

  //   /*
  //    * Strict seasonal identity boundary.
  //    */
  //   const taskplanid = Number(task.taskplanid ?? 0);

  //   if (!taskplanid) {
  //     this.setState({
  //       error: 'The selected Task does not have a seasonal Task Plan.',
  //     });

  //     return;
  //   }

  //   /*
  //    * IMPORTANT:
  //    *
  //    * This is the COMPLETE remaining active set.
  //    * Do not filter to dirty rows.
  //    */
  //   const rows = this.selectedBudgetRows();

  //   this.setState({
  //     saving: true,
  //     error: '',
  //   });

  //   try {
  //     await saveBudgetLines(taskplanid, rows);

  //     /*
  //      * Immediately reload DB-authoritative BudgetLines.
  //      *
  //      * This captures:
  //      * - permanent rid/rix
  //      * - generated shortcode
  //      * - authoritative amount
  //      * - server stamp
  //      * - account information
  //      */
  //     const authoritativeRows = await fetchBudgetLines(taskplanid);

  //     /*
  //      * Reload TaskPlan amounts from the authoritative
  //      * task resolver.
  //      */
  //     await this.reloadTasks();

  //     this.setState((state) => ({
  //       budgets: {
  //         ...state.budgets,

  //         [String(taskplanid)]: authoritativeRows,
  //       },

  //       dirtyBudgets: {
  //         ...state.dirtyBudgets,

  //         [String(taskplanid)]: false,
  //       },

  //       selectedBudgetLineId: null,
  //     }));

  //     /*
  //      * Refresh the parent Activity Planning workspace.
  //      *
  //      * The parent should preserve the selected Activity
  //      * while refreshing AP-014/workbench/register/cards.
  //      */
  //     await this.props.onSaved?.();
  //   } catch (error: any) {
  //     this.setState({
  //       error: error?.message || String(error),
  //     });
  //   } finally {
  //     this.setState({
  //       saving: false,
  //     });
  //   }
  // };
  private saveSelectedBudget = async () => {
    const task = this.state.selectedTask;

    if (!task) {
      return;
    }

    const taskplanid = Number(task.taskplanid ?? 0);

    if (!taskplanid) {
      this.setState({
        error: 'The selected Task does not have a seasonal Task Plan.',
      });

      return;
    }

    /*
     * Complete current working set.
     *
     * Do NOT filter by _state.
     */
    const rows = this.selectedBudgetRows();

    this.setState({
      saving: true,
      error: '',
    });

    try {
      /*
       * 1. Persist.
       *
       * budgetsave() owns:
       *
       * BudgetLine
       *   ↓
       * TaskPlan.plannedamount
       *   ↓
       * ActivityPlan.plannedamount
       */
      await saveBudgetLines(taskplanid, rows);

      /*
       * 2. Throw away the local BudgetLine cache for this
       *    TaskPlan.
       *
       * We explicitly invalidate it before doing any
       * resolver reload.
       */
      await new Promise<void>((resolve) => {
        this.setState((state) => {
          const budgets = {
            ...state.budgets,
          };

          delete budgets[String(taskplanid)];

          return {
            budgets,

            dirtyBudgets: {
              ...state.dirtyBudgets,

              [String(taskplanid)]: false,
            },

            selectedBudgetLineId: null,
          };
        }, resolve);
      });

      /*
       * 3. Reload authoritative Task resolver.
       *
       * This must reflect the TaskPlan rollup produced
       * by budgetsave().
       */
      await this.reloadTasks();

      /*
       * 4. Reload authoritative BudgetLines AFTER the
       *    cache has been invalidated.
       */
      const authoritativeRows = await fetchBudgetLines(taskplanid);

      /*
       * 5. Put only the DB result back into the cache.
       */
      await new Promise<void>((resolve) => {
        this.setState(
          (state) => ({
            budgets: {
              ...state.budgets,

              [String(taskplanid)]: authoritativeRows,
            },

            dirtyBudgets: {
              ...state.dirtyBudgets,

              [String(taskplanid)]: false,
            },
          }),
          resolve
        );
      });

      /*
       * 6. Refresh the parent Activity Planning workspace.
       *
       * This should refresh:
       * - ActivityPlan amount
       * - Register
       * - headline cards
       * - AP-014 tree
       *
       * while preserving the current strategic scope /
       * Activity selection.
       */
      await this.props.onSaved?.();
    } catch (error: any) {
      this.setState({
        error: error?.message || String(error),
      });
    } finally {
      this.setState({
        saving: false,
      });
    }
  };

  /*
   * -------------------------------------------------------------------------
   * Existing reusable Task lifecycle
   * -------------------------------------------------------------------------
   */

  private openAddExisting = async () => {
    const activityplanid = this.activityPlanId();

    if (!activityplanid) {
      return;
    }

    this.setState({
      addExistingOpen: true,
      loadingCandidates: true,
      selectedCandidateId: null,
      error: '',
    });

    try {
      const rows = await fetchTaskCandidates(activityplanid);

      this.setState({
        taskCandidates: rows.filter((row) => !row.planned && !row.taskplanid),
      });
    } catch (error: any) {
      this.setState({
        error: error?.message || String(error),

        addExistingOpen: false,
      });
    } finally {
      this.setState({
        loadingCandidates: false,
      });
    }
  };

  private candidateColumns = (): GridColDef[] => [
    {
      field: 'code',
      headerName: 'Task Code',
      width: 180,
    },
    {
      field: 'name',
      headerName: 'Task',
      minWidth: 420,
      flex: 1,
    },
    {
      field: 'description',
      headerName: 'Description',
      minWidth: 320,
      flex: 1,
    },
  ];

  private addExistingTask = async () => {
    const activityplanid = this.activityPlanId();

    const taskid = this.state.selectedCandidateId;

    if (!activityplanid || !taskid) {
      return;
    }

    this.setState({
      saving: true,
      error: '',
    });

    try {
      await savePlanningTasks(activityplanid, [
        {
          taskid,
        },
      ]);

      await this.reloadTasks();

      this.setState({
        addExistingOpen: false,

        selectedCandidateId: null,
      });

      /*
       * Refresh parent AP-014/workbench state.
       */
      await this.props.onSaved?.();
    } catch (error: any) {
      this.setState({
        error: error?.message || String(error),
      });
    } finally {
      this.setState({
        saving: false,
      });
    }
  };

  /*
   * -------------------------------------------------------------------------
   * Task reload
   * -------------------------------------------------------------------------
   */

  private reloadTasks = async () => {
    const activityplanid = this.activityPlanId();

    if (!activityplanid) {
      return;
    }

    const rows = await fetchTaskCandidates(activityplanid);

    const tasks = rows.filter((row) => !!row.planned || !!row.taskplanid);

    const selectedTask = this.state.selectedTask;

    const refreshedSelection = selectedTask
      ? (tasks.find(
          (row) => Number(row.taskplanid) === Number(selectedTask.taskplanid)
        ) ?? null)
      : null;

    const nextSelection = refreshedSelection ?? tasks[0] ?? null;

    this.setState(
      {
        tasks,

        selectedTask: nextSelection,
      },
      () => {
        if (nextSelection) {
          void this.ensureBudgetLoaded(nextSelection);
        }
      }
    );
  };

  /*
   * -------------------------------------------------------------------------
   * Create reusable Task lifecycle
   * -------------------------------------------------------------------------
   */

  private openCreateTask = () => {
    this.setState({
      createTaskOpen: true,

      newTaskName: '',
      newTaskDescription: '',
      newTaskWeight: 1,

      error: '',
    });
  };

  private createTask = async () => {
    const activityplanid = this.activityPlanId();

    const name = this.state.newTaskName.trim();

    if (!activityplanid || !name) {
      return;
    }

    this.setState({
      saving: true,
      error: '',
    });

    try {
      await savePlanningTasks(activityplanid, [
        {
          name,

          description: this.state.newTaskDescription.trim(),

          weight: Number(this.state.newTaskWeight) || 1,
        },
      ]);

      await this.reloadTasks();

      this.setState({
        createTaskOpen: false,

        newTaskName: '',
        newTaskDescription: '',
        newTaskWeight: 1,
      });

      /*
       * Refresh parent AP-014/workbench state.
       */
      await this.props.onSaved?.();
    } catch (error: any) {
      this.setState({
        error: error?.message || String(error),
      });
    } finally {
      this.setState({
        saving: false,
      });
    }
  };

  /*
   * -------------------------------------------------------------------------
   * Render
   * -------------------------------------------------------------------------
   */

  render() {
    const { open, activity } = this.props;

    const { tasks, selectedTask, loadingBudget, saving, error } = this.state;

    const summary = this.summary();

    const budgetRows = this.selectedBudgetRows();

    const activityCode = activity?.code ?? activity?.shortcode ?? '';

    const activityName = activity?.name ?? activity?.activityname ?? '';

    const taskCode =
      (selectedTask as any)?.code ?? (selectedTask as any)?.shortcode ?? '';

    const taskName = (selectedTask as any)?.name ?? '';

    /*
     * Working-set projection.
     *
     * This changes immediately while the planner edits.
     * DB remains authoritative after Save.
     */
    const taskBudget = budgetRows.reduce(
      (sum, row) => sum + Number(row.amount || 0),
      0
    );

    const dirty = this.selectedBudgetDirty();

    return (
      <>
        <Dialog
          open={open}
          onClose={saving ? undefined : this.props.onClose}
          fullWidth
          maxWidth={false}
          PaperProps={{
            sx: {
              width: '94vw',
              maxWidth: '94vw',
              height: '88vh',
              maxHeight: '88vh',
            },
          }}
        >
          <DialogTitle>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              spacing={2}
            >
              <Box>
                <Typography variant="h5" fontWeight={700}>
                  Task Planning
                </Typography>

                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  sx={{ mt: 0.5 }}
                >
                  {activityCode}

                  {activityCode && activityName ? ' — ' : ''}

                  {activityName}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  {this.props.costCentreName || ''}

                  {this.props.costCentreName && this.props.planningCycleName
                    ? ' · '
                    : ''}

                  {this.props.planningCycleName || ''}
                </Typography>
              </Box>

              <Button
                size="small"
                startIcon={<CloseIcon />}
                disabled={saving}
                onClick={this.props.onClose}
              >
                Close
              </Button>
            </Stack>
          </DialogTitle>

          <Divider />

          <DialogContent
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              overflow: 'hidden',
            }}
          >
            {error && <Alert severity="error">{error}</Alert>}

            {/* Summary */}

            <Stack direction="row" spacing={1.5}>
              <SummaryCard label="Tasks" value={summary.taskCount} />

              <SummaryCard label="Budgeted" value={summary.budgeted} />

              <SummaryCard label="Unbudgeted" value={summary.unbudgeted} />

              <SummaryCard
                label="Activity Budget"
                value={this.money(summary.total)}
              />
            </Stack>

            {/* Task Master */}

            <Paper
              variant="outlined"
              sx={{
                p: 1,
                flex: '0 0 38%',
                minHeight: 0,
              }}
            >
              <FDataGrid
                title="Tasks"
                subtitle="Select a Task to review its budget lines."
                rows={tasks}
                columns={this.taskColumns()}
                height="100%"
                selectionMode="single"
                getRowId={(row: any) => this.taskRowId(row as TaskRow)}
                selectedRowId={
                  selectedTask ? this.taskRowId(selectedTask) : null
                }
                onSelectedRowChange={(row, _rowId) => {
                  if (row) {
                    this.selectTask(row as TaskRow);
                  }
                }}
                processRowUpdate={(newRow: any, oldRow: any) =>
                  this.updateTaskRow(newRow as TaskRow, oldRow as TaskRow)
                }
                toolbarActions={[
                  {
                    key: 'add-existing-task',

                    label: 'Add Existing Task',

                    icon: <AddIcon fontSize="small" />,

                    onClick: this.openAddExisting,
                  },

                  {
                    key: 'create-task',

                    label: 'Create New Task',

                    icon: <AddIcon fontSize="small" />,

                    onClick: this.openCreateTask,
                  },
                ]}
              />
            </Paper>

            {/* Budget Detail */}

            <Paper
              variant="outlined"
              sx={{
                p: 1,
                flex: 1,
                minHeight: 0,
              }}
            >
              {!selectedTask && (
                <Alert severity="info">
                  Select a Task to review its budget lines.
                </Alert>
              )}

              {selectedTask && (
                <FDataGrid
                  title={
                    taskCode ? `Budget Lines — ${taskCode}` : 'Budget Lines'
                  }
                  subtitle={taskName}
                  rows={budgetRows}
                  columns={this.budgetColumns()}
                  loading={loadingBudget}
                  height="100%"
                  getRowId={(row: any) => row.id}
                  selectionMode="single"
                  selectedRowId={this.state.selectedBudgetLineId}
                  onSelectedRowChange={(row, rowId) =>
                    this.setState({
                      selectedBudgetLineId: row
                        ? (rowId ?? (row as any).id)
                        : null,
                    })
                  }
                  /*
                   * FDataGrid is expected to pass the MUI
                   * processRowUpdate contract through.
                   */
                  processRowUpdate={(newRow: any) =>
                    this.updateBudgetRow(newRow as BudgetLineRow)
                  }
                  toolbarActions={[
                    {
                      key: 'add-budget-line',

                      label: 'Add Budget Line',

                      icon: <AddIcon fontSize="small" />,

                      onClick: this.addBudgetLine,
                    },

                    {
                      key: 'remove-budget-line',

                      label: 'Remove Budget Line',

                      icon: <DeleteIcon fontSize="small" />,

                      disabled: this.state.selectedBudgetLineId === null,

                      onClick: this.removeSelectedBudgetLine,
                    },
                  ]}
                />
              )}
            </Paper>
          </DialogContent>

          <Divider />

          <DialogActions
            sx={{
              px: 3,
              py: 1.5,
              justifyContent: 'space-between',
            }}
          >
            <Stack direction="row" spacing={3}>
              <Typography variant="body2">
                Task Budget: <strong>{this.money(taskBudget)}</strong>
                {dirty ? ' *' : ''}
              </Typography>

              <Typography variant="body2">
                Activity Budget: <strong>{this.money(summary.total)}</strong>
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1}>
              <Button disabled={saving} onClick={this.props.onClose}>
                Close
              </Button>

              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={
                  saving || loadingBudget || !selectedTask?.taskplanid || !dirty
                }
                onClick={this.saveSelectedBudget}
              >
                Save Budget
              </Button>
            </Stack>
          </DialogActions>
        </Dialog>

        {/* Existing Task */}

        <Dialog
          open={this.state.addExistingOpen}
          onClose={() =>
            !this.state.saving &&
            this.setState({
              addExistingOpen: false,
            })
          }
          fullWidth
          maxWidth="lg"
        >
          <DialogTitle>Add Existing Task</DialogTitle>

          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Select a reusable Task from the repository for this Activity.
            </Typography>

            <FDataGrid
              rows={this.state.taskCandidates}
              columns={this.candidateColumns()}
              loading={this.state.loadingCandidates}
              height={420}
              getRowId={(row: any) => row.taskid ?? row.id}
              selectionMode="single"
              selectedRowId={this.state.selectedCandidateId}
              onSelectedRowChange={(row) =>
                this.setState({
                  selectedCandidateId: row
                    ? Number((row as TaskRow).taskid)
                    : null,
                })
              }
            />
          </DialogContent>

          <DialogActions>
            <Button
              disabled={this.state.saving}
              onClick={() =>
                this.setState({
                  addExistingOpen: false,
                })
              }
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              disabled={this.state.saving || !this.state.selectedCandidateId}
              onClick={this.addExistingTask}
            >
              Add Task
            </Button>
          </DialogActions>
        </Dialog>

        {/* New Task */}

        <Dialog
          open={this.state.createTaskOpen}
          onClose={() =>
            !this.state.saving &&
            this.setState({
              createTaskOpen: false,
            })
          }
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Create New Task</DialogTitle>

          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Task Name"
                required
                fullWidth
                value={this.state.newTaskName}
                onChange={(event) =>
                  this.setState({
                    newTaskName: event.target.value,
                  })
                }
              />

              <TextField
                label="Description"
                fullWidth
                multiline
                minRows={3}
                value={this.state.newTaskDescription}
                onChange={(event) =>
                  this.setState({
                    newTaskDescription: event.target.value,
                  })
                }
              />

              <TextField
                label="Weight"
                type="number"
                fullWidth
                inputProps={{
                  min: 0,
                  step: 0.01,
                }}
                value={this.state.newTaskWeight}
                onChange={(event) =>
                  this.setState({
                    newTaskWeight: Number(event.target.value),
                  })
                }
              />

              <Alert severity="info">
                The Task code will be generated automatically and the Task will
                become reusable under this Activity.
              </Alert>
            </Stack>
          </DialogContent>

          <DialogActions>
            <Button
              disabled={this.state.saving}
              onClick={() =>
                this.setState({
                  createTaskOpen: false,
                })
              }
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              disabled={this.state.saving || !this.state.newTaskName.trim()}
              onClick={this.createTask}
            >
              Create Task
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }
}

type SummaryCardProps = {
  label: string;
  value: React.ReactNode;
};

class SummaryCard extends React.PureComponent<SummaryCardProps> {
  render() {
    return (
      <Paper
        variant="outlined"
        sx={{
          px: 2,
          py: 1,
          minWidth: 150,
          flex: 1,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {this.props.label}
        </Typography>

        <Typography variant="h6" fontWeight={700}>
          {this.props.value}
        </Typography>
      </Paper>
    );
  }
}
